import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AddOnRateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import { AddOn } from '~hooks/useAddOns/models';
import { FuelSurchargeScheduleTypeahead } from '~hooks/useFuelSurchargeSchedule/model';
import { useFuelSurchargeSchedule } from '~hooks/useFuelSurchargeSchedule/useFuelSurchargeSchedule';
import { useStores } from '~store';

import {
  AddOnDTO,
  addOnRateTypeOptions,
  addOnSchema,
  getAddOnFormDefaultValues,
} from './addOnFormSchema';

export interface AddOnFormHandler {
  onSubmit?: () => Promise<AddOnDTO>;
}

interface AddOnFormProps {
  addOn?: AddOn;
  defaultAddOnName?: string;
  sx?: SxProps<Theme>;
}

const AddOnForm = forwardRef<AddOnFormHandler, AddOnFormProps>(function AddOnForm(
  { addOn, defaultAddOnName, sx },
  ref,
) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    setValue,
  } = useForm<AddOnDTO>({
    resolver: yupResolver(addOnSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: getAddOnFormDefaultValues(addOn, defaultAddOnName),
  });

  const { userStore } = useStores();
  const userCompany = userStore.currentCompanies[0];
  const { getFuelSurchargeSchedulesTypeahead } = useFuelSurchargeSchedule();

  useEffect(() => {
    if (!addOn?.id) {
      reset(getAddOnFormDefaultValues(addOn, defaultAddOnName));
    }
  }, [userStore.currentCompanies, addOn, defaultAddOnName, reset]);

  const watchedRevenueCostRateType = watch('revenueCostRateType');
  const isFuelSurcharge = watchedRevenueCostRateType?.id === AddOnRateType.FUEL_SURCHARGE;

  useEffect(() => {
    if (isFuelSurcharge) {
      setValue('costRateValue', undefined);
      setValue('rate', undefined);
      setValue('quantity', undefined);
    } else {
      setValue('fuelSurchargeSchedule', {
        id: '',
        name: '',
      } as FuelSurchargeScheduleTypeahead);
    }
  }, [isFuelSurcharge, setValue]);

  const revenueCostRateTypeAdornmentText = useMemo(() => {
    if (
      watchedRevenueCostRateType?.id === AddOnRateType.RATE_FOR_EACH ||
      watchedRevenueCostRateType?.id === AddOnRateType.PER_LOAD
    ) {
      return '$';
    }

    if (watchedRevenueCostRateType?.id === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
      return '%';
    }

    return '';
  }, [watchedRevenueCostRateType]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit((data) => {
            return resolve(data);
          })();
        });
      },
    }),
    [],
  );

  return (
    <Box sx={[{ mt: 2.5 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 3,
        }}
      >
        {!addOn?.id && (
          <CompanySelectorFormField
            control={control}
            errors={errors}
            name={'company'}
            disabled
            sx={{ gridColumn: 'span 2' }}
            defaultValue={userCompany}
          />
        )}

        <TextFormField
          control={control}
          errors={errors}
          isRequired
          label={`${t('common.description')}`}
          name="name"
        />

        <AutocompleteFormField
          clearable
          control={control}
          errors={errors}
          getLabel={(item: (typeof addOnRateTypeOptions)[number]) => item.name}
          getValue={(item: (typeof addOnRateTypeOptions)[number]) => item.id}
          isRequired
          label={`${t('form_fields.rate_type')}`}
          list={addOnRateTypeOptions}
          name="revenueCostRateType"
          dataUi="add-on-form__revenue-cost-rate-type-input"
        />

        {isFuelSurcharge && (
          <AutocompleteAsyncFormField
            clearable
            control={control}
            errors={errors}
            isRequired
            label={`${t('administration.rates.add_ons.fuel_surcharge_schedule')}`}
            name="fuelSurchargeSchedule"
            asyncCallback={async (options: { query?: string; cursor?: string }) => {
              const result = await getFuelSurchargeSchedulesTypeahead({
                searchQuery: options.query,
                cursor: options.cursor,
              });
              return result;
            }}
            getValue={(item: FuelSurchargeScheduleTypeahead | null) => item?.id || ''}
            getLabel={(item: FuelSurchargeScheduleTypeahead | null) => item?.name || ''}
            sx={{ gridColumn: 'span 2' }}
          />
        )}

        {!isFuelSurcharge && (
          <>
            <TextFormField
              InputProps={{
                ...(revenueCostRateTypeAdornmentText === '%'
                  ? {
                      endAdornment: (
                        <Typography
                          sx={{
                            mr: 1,
                            fontSize: 'inherit',
                          }}
                        >
                          {revenueCostRateTypeAdornmentText}
                        </Typography>
                      ),
                    }
                  : {
                      startAdornment: (
                        <Typography
                          sx={{
                            mr: 1,
                            fontSize: 'inherit',
                          }}
                        >
                          {revenueCostRateTypeAdornmentText}
                        </Typography>
                      ),
                    }),
              }}
              inputProps={
                revenueCostRateTypeAdornmentText === '%' ? { step: '0.01' } : undefined
              }
              control={control}
              errors={errors}
              label={`${t('administration.rates.add_ons.cost_rate')}`}
              name="costRateValue"
              type="number"
            />

            <TextFormField
              InputProps={{
                ...(revenueCostRateTypeAdornmentText === '%'
                  ? {
                      endAdornment: (
                        <Typography
                          sx={{
                            mr: 1,
                            fontSize: 'inherit',
                          }}
                        >
                          {revenueCostRateTypeAdornmentText}
                        </Typography>
                      ),
                    }
                  : {
                      startAdornment: (
                        <Typography
                          sx={{
                            mr: 1,
                            fontSize: 'inherit',
                          }}
                        >
                          {revenueCostRateTypeAdornmentText}
                        </Typography>
                      ),
                    }),
              }}
              inputProps={
                revenueCostRateTypeAdornmentText === '%' ? { step: '0.0001' } : undefined
              }
              control={control}
              errors={errors}
              label={`${t('administration.rates.invoice_rate')}`}
              name="rate"
              type="number"
            />

            <TextFormField
              control={control}
              errors={errors}
              label={`${t('administration.rates.add_ons.default_quantity')}`}
              name="quantity"
              type="number"
            />
          </>
        )}

        <TextFormField
          control={control}
          errors={errors}
          label={`${t('form_fields.external_id')}`}
          name="externalId"
        />

        <TextFormField
          control={control}
          errors={errors}
          label={`${t('form_fields.accounting_id')}`}
          name="accountingId"
        />

        <CheckboxInput
          control={control}
          label={t('administration.rates.add_ons.taxable')}
          name="taxable"
          sx={{ gridColumn: 'span 2' }}
        />
      </Box>
    </Box>
  );
});

export default AddOnForm;
